import { useMemo, useState } from 'react';
import type { Account, Snapshot } from '../types';
import { accountTouches } from '../engine/projection';
import { CompositionBar, type CompositionSlice } from './CompositionBar';
import { AccountCard } from './AccountCard';
import { SegmentedControl } from '../ui';

const KIND_COLOR: Record<Account['kind'], string> = {
  hysa: '#0891b2',        // cyan
  checking: '#06b6d4',    // lighter cyan
  taxable: '#f59e0b',     // amber
  retirement: '#8b5cf6',  // violet
  home: '#14b8a6',        // teal
  mortgage: '#ef4444',    // red
  other_asset: '#64748b', // slate
  other_liability: '#dc2626', // deep red
};

export function AccountsPanel({
  accounts,
  snapshots,
  currentBalances,
  onEdit,
}: {
  accounts: Account[];
  snapshots: Snapshot[];
  currentBalances: Record<string, number>;
  onEdit: () => void;
}) {
  const [view, setView] = useState<'assets' | 'liabilities'>('assets');
  const touches = useMemo(() => accountTouches(snapshots, accounts), [snapshots, accounts]);

  const partitioned = useMemo(() => {
    const assets: Account[] = [];
    const liabs: Account[] = [];
    for (const a of accounts) {
      if (a.kind === 'mortgage' || a.kind === 'other_liability') liabs.push(a);
      else assets.push(a);
    }
    return { assets, liabs };
  }, [accounts]);

  const shown = view === 'assets' ? partitioned.assets : partitioned.liabs;

  const slices: CompositionSlice[] = shown
    .map(a => ({
      id: a.id,
      label: a.name,
      value: Math.max(0, currentBalances[a.id] ?? 0),
      color: KIND_COLOR[a.kind],
    }))
    .filter(s => s.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = slices.reduce((s, x) => s + x.value, 0);

  const sortedAccounts = [...shown]
    .map(a => ({
      account: a,
      value: currentBalances[a.id] ?? 0,
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <section className="card card-dark overflow-hidden">
      <div className="flex items-center justify-between border-b border-ink-100 px-4 py-2 dark:border-ink-700">
        <div className="label">{view === 'assets' ? 'Assets' : 'Liabilities'}</div>
        <div className="flex items-center gap-3">
          <SegmentedControl
            size="sm"
            value={view}
            onChange={setView}
            options={[
              { value: 'assets', label: 'Assets' },
              { value: 'liabilities', label: 'Liabs' },
            ]}
          />
          <button
            className="text-[11px] font-semibold text-accent-600 dark:text-accent-500"
            onClick={onEdit}
          >
            Manage →
          </button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {slices.length > 0 ? (
          <CompositionBar
            slices={slices}
            ariaLabel={`${view === 'assets' ? 'Asset' : 'Liability'} composition`}
          />
        ) : (
          <p className="py-4 text-center text-xs text-ink-500 dark:text-ink-300">No {view} recorded yet.</p>
        )}

        <ul className="space-y-2">
          {sortedAccounts.map(({ account, value }, i) => (
            <li key={account.id}>
              <AccountCard
                account={account}
                value={value}
                share={total > 0 ? value / total : 0}
                color={KIND_COLOR[account.kind]}
                touches={touches[account.id] ?? []}
                rank={i + 1}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
